"use client"

import * as React from "react"
import { X, LucideIcon } from 'lucide-react'
import {
  UICard,
  UICardContent,
  UICardFooter,
  UICardHeader,
  UICardTitle,
  UICardDescription
} from './ui-card'
import { UIButton } from '../buttons/ui-button'
import { UITypography } from '../display/ui-typography'
import { cn } from '../../lib/utils'
import { UIBox } from '../layout/ui-box'

export interface UICardBannerProps
  extends Omit<React.ComponentProps<typeof UICard>, 'children'> {
  title: string
  description?: string
  icon?: LucideIcon
  variant?: 'default' | 'accent' | 'success' | 'warning' | 'destructive'
  dismissible?: boolean
  onDismiss?: () => void
  primaryAction?: {
    text: string
    onClick: () => void
    variant?: 'default' | 'accent' | 'outline'
  }
  secondaryAction?: {
    text: string
    onClick: () => void
    variant?: 'outline' | 'ghost'
  }
  children?: React.ReactNode
}

/**
 * Banner Card Component
 * Promotional or announcement card with prominent styling
 * Based on shadcn/ui "Banner Card" block
 */
export const UICardBanner = React.forwardRef<HTMLDivElement, UICardBannerProps>(
  ({
    title,
    description,
    icon: Icon,
    variant = 'default',
    dismissible = false,
    onDismiss,
    primaryAction,
    secondaryAction,
    className,
    children,
    ...props
  }, ref) => {
    const [dismissed, setDismissed] = React.useState(false)

    const handleDismiss = () => {
      setDismissed(true)
      onDismiss?.()
    }

    if (dismissed) return null

    const variantStyles = {
      default: "border-border bg-card",
      accent: "border-accent/20 bg-accent/10 dark:bg-accent/5",
      success: "border-green-200 bg-green-50 dark:border-green-800 dark:bg-green-950/20",
      warning: "border-yellow-200 bg-yellow-50 dark:border-yellow-800 dark:bg-yellow-950/20",
      destructive: "border-red-200 bg-red-50 dark:border-red-800 dark:bg-red-950/20"
    }

    const iconStyles = {
      default: "text-accent",
      accent: "text-accent",
      success: "text-green-600 dark:text-green-400",
      warning: "text-yellow-600 dark:text-yellow-400",
      destructive: "text-red-600 dark:text-red-400"
    }

    return (
      <UICard
        ref={ref}
        className={cn(
          "relative",
          variantStyles[variant],
          className
        )}
        {...props}
      >
        {/* Dismiss button */}
        {dismissible && (
          <button
            onClick={handleDismiss}
            className="absolute right-4 top-4 rounded-sm opacity-70 hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
          >
            <X className="h-4 w-4" />
            <UITypography as="span" variant="small" className="sr-only">Dismiss</UITypography>
          </button>
        )}

        {/* Header */}
        <UICardHeader className={dismissible ? "pr-10" : ""}>
          <UIBox className="flex items-start gap-3">
            {Icon && (
              <UIBox className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-background/80 dark:bg-background/40">
                <Icon className={cn("h-5 w-5", iconStyles[variant])} />
              </UIBox>
            )}

            <UIBox className="min-w-0 flex-1">
              <UICardTitle>
                <UITypography variant="h3" size="lg" weight="semibold">
                  {title}
                </UITypography>
              </UICardTitle>

              {description && (
                <UICardDescription className="mt-1">
                  <UITypography variant="p" size="sm" color="muted">
                    {description}
                  </UITypography>
                </UICardDescription>
              )}
            </UIBox>
          </UIBox>
        </UICardHeader>

        {/* Content */}
        {children && (
          <UICardContent className="py-4">
            {children}
          </UICardContent>
        )}

        {/* Footer with actions */}
        {(primaryAction || secondaryAction) && (
          <UICardFooter className="pt-4">
            <UIBox className="flex w-full gap-2">
              {primaryAction && (
                <UIButton
                  variant={primaryAction.variant || 'default'}
                  onClick={primaryAction.onClick}
                  className="flex-1"
                >
                  {primaryAction.text}
                </UIButton>
              )}
              {secondaryAction && (
                <UIButton
                  variant={secondaryAction.variant || 'outline'}
                  onClick={secondaryAction.onClick}
                  className={primaryAction ? "flex-1" : "w-full"}
                >
                  {secondaryAction.text}
                </UIButton>
              )}
            </UIBox>
          </UICardFooter>
        )}
      </UICard>
    )
  }
)

UICardBanner.displayName = "UICardBanner"